import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import {
  KnowledgeNodeResponse,
  KnowledgeNodeUpdate,
  useCreateKnowledgeNode,
  useDeleteKnowledgeNode,
  useUpdateKnowledgeNode,
} from '@/entities/knowledge';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { createKnowledgeFormSchema, KnowledgeFormValues } from './types';

const isEmptyPredicate = (predicate?: { dsl?: string; dslHuman?: string } | null): boolean => {
  if (!predicate) return true;
  const dsl = predicate.dsl?.trim();
  const dslHuman = predicate.dslHuman?.trim();
  return !dsl && !dslHuman;
};

type Params = {
  node?: KnowledgeNodeResponse | null;
  isCreate: boolean;
};

export const useKnowledgeForm = (params: Params) => {
  const { node, isCreate } = params;

  const { t } = useTranslation(['knowledge', 'common']);
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_KNOWLEDGE');

  const { mutateAsync: updateNode, isPending: isUpdating } = useUpdateKnowledgeNode();
  const { mutateAsync: deleteNode, isPending: isDeleting } = useDeleteKnowledgeNode();
  const { mutateAsync: createNode, isPending: isCreating } = useCreateKnowledgeNode();

  const isLoading = isCreating || isUpdating;

  const schema = useMemo(() => createKnowledgeFormSchema(t), [t]);

  const defaults: KnowledgeFormValues = useMemo(
    () => ({
      name: node?.name ?? '',
      text: node?.text ?? '',
      predicate: node?.predicate ?? { dsl: '', dslHuman: '' },
    }),
    [node],
  );

  const form = useForm<KnowledgeFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    disabled: !allowAdmin,
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  const buildPayload = (values: KnowledgeFormValues): KnowledgeNodeUpdate => {
    const payload: KnowledgeNodeUpdate = {
      name: values.name,
      text: values.text,
    };

    if (!isEmptyPredicate(values.predicate) && values.predicate?.dsl) {
      payload.predicate = { dsl: values.predicate.dsl, dslHuman: values.predicate.dslHuman };
    }

    return payload;
  };

  const onSubmit = form.handleSubmit(async (values) => {
    const payload = buildPayload(values);

    if (isCreate) {
      const res = await createNode(payload);
      if (res?.id) {
        navigate('/knowledge/:id', { id: String(res.id) });
      } else {
        navigate('/knowledge');
      }
      return;
    }

    if (node) {
      await updateNode({ id: node.id, payload });
    }
  });

  const handleTitleSave = async (value: string) => {
    form.setValue('name', value, { shouldDirty: true });

    if (isCreate || !node) {
      return;
    }

    const currentValues = form.getValues();
    const payload = buildPayload({ ...currentValues, name: value });

    await updateNode({ id: node.id, payload });
  };

  const handleDelete = async () => {
    if (node) {
      await deleteNode(node.id);
      navigate('/knowledge');
    }
  };

  return {
    form,
    isLoading,
    isDeleting,
    isDisabled: !allowAdmin,
    onSubmit,
    handleTitleSave,
    handleDelete,
  } as const;
};
